import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { auth, db } from "../../firebase";
import {
    collection,
    query,
    orderBy,
    onSnapshot,
    getDoc,
    doc,
    limit
} from "firebase/firestore";

import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

export default function Userlogs() {
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
    });

    const [userData, setUserData] = useState(null);
    const [loadincount, setLoadincount] = useState(true);

    const [logs, setLogs] = useState([]);
    const [allLogs, setAllLogs] = useState([]);
    const [searchText, setSearchText] = useState("");

    /* 🔐 AUTH */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(user => {
            setUserData(user);
        });
        return () => unsub();
    }, []);

    /* 📌 FETCH USER + LOGS */
    useEffect(() => {
        if (!userData || !loadincount) return;

        const fetchUser = async () => {
            try {
                const docSnap = await getDoc(doc(db, "users", userData.uid));
                if (docSnap.exists()) {
                    fetchLogs();
                    setLoadincount(false);
                }
            } catch (err) {
                Toast.fire({ icon: "error", title: err.message });
            }
        };

        fetchUser();
    }, [userData]);

    /* 📊 FETCH LOGS (REALTIME) */
    const fetchLogs = async () => {
        try {
            const q = query(
                collection(db, "userlogs"),
                orderBy("createdAt", "desc"),
                limit(500)
            );

            onSnapshot(q, snap => {
                const data = snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setLogs(data);
                setAllLogs(data);
            });
        } catch (err) {
            Toast.fire({ icon: "error", title: err.message });
        }
    };

    /* 🔍 SEARCH */
    useEffect(() => {
        if (!searchText) {
            setLogs(allLogs);
        } else {
            const filtered = allLogs.filter(item =>
                item.username?.toUpperCase().includes(searchText.toUpperCase())
            );
            setLogs(filtered);
        }
    }, [searchText, allLogs]);

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    textTransform="uppercase"
                    textAlign="right"
                    mb={3}
                    sx={{ color: colors.vanilla }}
                >
                    || User Logs ||
                </Typography>

                <Card sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <CardContent sx={{ p: 3 }}>
                        <div className="container-fluid p-0 mb-3">
                            <div className="row">
                                <div className="col-sm-4">
                                    <TextField
                                        type="search"
                                        label="Search User"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ style: { color: colors.vanilla } }}
                                        InputProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent" }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: colors.darkGreen }}>
                                    <TableRow>
                                        <TableCell width={80} sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Sr No</TableCell>
                                        <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>User Name</TableCell>
                                        <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Type</TableCell>
                                        <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Customer Name</TableCell>
                                        <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Customer Mobile</TableCell>
                                        <TableCell align="center" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {logs.map((item, index) => (
                                        <TableRow key={item.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                            <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{index + 1}</TableCell>
                                            <TableCell sx={{ color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{item.username}</TableCell>
                                            <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{item.type}</TableCell>
                                            <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{item.customers?.custname}</TableCell>
                                            <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{item.customers?.mobile}</TableCell>
                                            <TableCell align="center" sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                                <IconButton
                                                    sx={{ color: colors.gold }}
                                                    component="a"
                                                    href={`view-details/${item.bookingid}`}
                                                    size="small"
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {!logs.length && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                                                No logs found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}